import { SlotStatus } from 'clinical-config/config/types';
import {
  DayIndexes,
  isDayAvailable,
  SLOT_SEQUENCE_EXT,
  weekDay,
} from 'clinician-config/constants';
import {
  convertTimeToDate,
  removeTempSlotIDs,
  sortFreeSlotsByStartTime,
  TEMP_TEXT,
} from 'clinician-config/PreferenceBuilder';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import isValid from 'date-fns/isValid';
import {
  EXTENSION_URL_PATIENT_EXCEPTION_ENDTIME,
  EXTENSION_URL_PATIENT_EXCEPTION_STARTTIME,
  PCP_SINCE_EXTENSION_URL,
  UNABLE_TO_OBTAIN_EMAIL_EXTENSION_URL,
  US_CORE_OMB_CATEGORY_SUB_EXTENSION_URL,
  US_CORE_RACE_DETAILED_SUB_EXTENSION_URL,
  US_CORE_RACE_EXTENSION_URL,
  US_CORE_TEXT_SUB_EXTENSION_URL,
} from 'patient/constants';
import { ISlotWithOperationType, PatientAvailability } from 'patient/types';
import uuid from 'react-uuid';
import { dateStringToObject } from 'styleguide/form/Date/utils';
import {
  getTimeFromZonedDateTime,
  transformInput,
} from 'styleguide/form/Time/utils';
import { ResourceType } from 'utility/constants';
import { isArrayEmpty } from 'utility/helpers';

export const defaultStartTime = '';
export const defaultEndTime = '';

export function getWeekDay(slot: fhir.Slot) {
  return slot?.extension?.find((item) => item.url === weekDay)
    ?.valuePositiveInt;
}
export function getSlotSequence(slot: fhir.Slot) {
  return slot?.extension?.find((ext) => ext.url === SLOT_SEQUENCE_EXT)
    ?.valueInteger;
}
export const isGeneralWeekday = (week, slot) => {
  if (
    getWeekDay(week) === 1 &&
    getWeekDay(slot) !== 0 &&
    getWeekDay(slot) !== 6
  ) {
    return true;
  }
  return false;
};
export const isGeneralWeekend = (week, slot) => {
  if (
    getWeekDay(week) === 0 &&
    (getWeekDay(slot) === 0 || getWeekDay(slot) === 6)
  ) {
    return true;
  }
  return false;
};

export const checkEmptyName = (name) => {
  let check = true;

  if (!name || typeof name === 'undefined' || Object.keys(name).length === 0) {
    return true;
  }
  for (const item in name) {
    if (typeof name[item] === 'undefined') {
      check = check && true;
    }
    if (item !== 'use' && !name[item] && typeof name[item] === 'string') {
      check = check && name[item] === '';
    }
    if (name[item] instanceof Array) {
      const isItemEmpty = isArrayEmpty(name[item]);
      check = check && isItemEmpty;
    }
  }
  return check;
};
export const checkTelecomIsEmpty = (telecom, type) => {
  let check = false;
  if (typeof telecom === 'undefined' || Object.keys(telecom).length === 0) {
    return true;
  }
  if (
    type === 'phone' &&
    (telecom?.use === '' || typeof telecom?.use === 'undefined') &&
    telecom?.value === ''
  ) {
    check = true;
  }
  if (type === 'email' && telecom?.value === '') check = true;
  return check;
};
export const checkIfRelationshipIsEmpty = (relation) => {
  if (typeof relation === 'undefined' || Object.keys(relation).length === 0) {
    return true;
  }
};
export const checkIfEmpty = (listOfObjects) => {
  return listOfObjects.map(
    (object) =>
      checkEmptyName(object?.name) &&
      checkIfRelationshipIsEmpty(object?.relationship?.[0]) &&
      checkTelecomIsEmpty(
        object?.telecom?.filter((telecom) => telecom.system === 'phone')?.[0],
        'phone'
      ) &&
      checkTelecomIsEmpty(
        object?.telecom?.filter((telecom) => telecom.system === 'email')?.[0],
        'email'
      )
  );
};

export const removeEmptyObjects = (listOfObjects) => {
  if (typeof listOfObjects === 'undefined') return;

  const emptyObjects = checkIfEmpty(listOfObjects);
  const values = [...listOfObjects];
  emptyObjects.forEach((value, index) => {
    if (value) {
      values.splice(index, 1);
    }
  });
  return values;
};

/**
 * Strips the embedded `resource` (read-shape only, e.g. from a GraphQL query
 * response) from each generalPractitioner entry, since the `updatePatient`/
 * `createPatient` mutation's `ReferenceInput` only accepts `reference`. Use
 * `extractPcpSinceExtension` first if the PCP-since date needs preserving —
 * that value only lives on `resource.extension` while editing, so it would
 * otherwise be silently dropped here.
 */
export const removeResourceFromPractitioner = (practitionerList) => {
  if (!Array.isArray(practitionerList)) return;
  return practitionerList.map(({ resource, reference, ...item }) => ({
    reference,
    ...item,
  }));
};

/**
 * Pulls the PCP-since date out of `generalPractitioner[0].resource.extension`
 * (where `setPcpSince` stores it while editing) so callers can relocate it
 * onto `patient.extension` before submitting — `getPcpSince` already reads it
 * from there as a fallback.
 */
export const extractPcpSinceExtension = (practitionerList) => {
  const resourceExtensions = practitionerList?.[0]?.resource?.extension;
  return resourceExtensions?.find(
    (extension) => extension.url === PCP_SINCE_EXTENSION_URL
  );
};

/** Legacy flat extension reader, e.g. `{url: 'RACE', valueCodeableConcept: {...}}`. */
export const getRaceOrEthnicity = (data, type) => {
  return data?.extension?.find((item) => item.url === type)
    ?.valueCodeableConcept;
};

/**
 * Reads every `valueCoding` from a named sub-extension (e.g. `ombCategory`,
 * `detailed`) nested under a proper US Core complex extension (e.g.
 * `us-core-race`) on `Patient.extension`.
 */
export const getUsCoreSubExtensionCodings = (
  data,
  extensionUrl: string,
  subExtensionUrl: string
): fhir.Coding[] => {
  const usCoreExtension = data?.extension?.find(
    (item) => item.url === extensionUrl
  );
  return (usCoreExtension?.extension ?? [])
    .filter(
      (sub) => sub.url === subExtensionUrl && Boolean(sub.valueCoding?.code)
    )
    .map((sub) => sub.valueCoding);
};

/** Detailed/granular race codings, nested under the `us-core-race` extension. */
export const getDetailedRaceCodings = (data): fhir.Coding[] =>
  getUsCoreSubExtensionCodings(
    data,
    US_CORE_RACE_EXTENSION_URL,
    US_CORE_RACE_DETAILED_SUB_EXTENSION_URL
  );

/**
 * Builds a single, spec-shaped `us-core-race`/`us-core-ethnicity` extension
 * from its `ombCategory` and (race-only) `detailed` codings. `text` (required
 * 1..1 by US Core) is the comma-joined display of the category codings.
 * Returns `null` when there's nothing to store, so the caller can remove the
 * extension entirely.
 */
export const buildUsCoreExtension = (
  extensionUrl: string,
  ombCategoryCodings: fhir.Coding[],
  detailedCodings: fhir.Coding[] = []
) => {
  if (!ombCategoryCodings.length && !detailedCodings.length) {
    return null;
  }

  const text = ombCategoryCodings
    .map((coding) => coding.display)
    .filter((display): display is string => Boolean(display))
    .join(', ');

  return {
    url: extensionUrl,
    extension: [
      ...ombCategoryCodings.map((valueCoding) => ({
        url: US_CORE_OMB_CATEGORY_SUB_EXTENSION_URL,
        valueCoding,
      })),
      ...detailedCodings.map((valueCoding) => ({
        url: US_CORE_RACE_DETAILED_SUB_EXTENSION_URL,
        valueCoding,
      })),
      ...(text
        ? [{ url: US_CORE_TEXT_SUB_EXTENSION_URL, valueString: text }]
        : []),
    ],
  };
};

/**
 * Reads race/ethnicity category codings, preferring the proper US Core
 * `ombCategory` sub-extension and falling back to the legacy flat extension
 * (`RACE`/`ETHNICITY`) for patients never re-saved under the new shape —
 * including patients imported via the CQM/QRDA pipeline, which always writes
 * the legacy shape. Returns the same `{coding, text}` shape as
 * `getRaceOrEthnicity` so existing call sites need minimal changes.
 */
export const getRaceOrEthnicityWithFallback = (
  data,
  usCoreExtensionUrl: string,
  legacyType: string
) => {
  const ombCategoryCodings = getUsCoreSubExtensionCodings(
    data,
    usCoreExtensionUrl,
    US_CORE_OMB_CATEGORY_SUB_EXTENSION_URL
  );

  if (ombCategoryCodings.length) {
    const textSub = data?.extension
      ?.find((item) => item.url === usCoreExtensionUrl)
      ?.extension?.find((sub) => sub.url === US_CORE_TEXT_SUB_EXTENSION_URL);
    return {
      coding: ombCategoryCodings,
      text: textSub?.valueString,
    };
  }

  return getRaceOrEthnicity(data, legacyType);
};

export const getUnableToObtainEmail = (data) => {
  const unableToObtainEmail = data?.extension?.find(
    (item) => item?.url === UNABLE_TO_OBTAIN_EMAIL_EXTENSION_URL
  )?.valueBoolean;
  return unableToObtainEmail ? 'Unable to obtain email' : null;
};

export const removeResourceFromExtention = (extensionList) => {
  if (!Array.isArray(extensionList)) return;
  const newextensionList = extensionList.map(({ valueReference, ...item }) => {
    if (valueReference && Object.keys(valueReference).includes('resource')) {
      const { resource, ...otherItems } = valueReference;
      return { ...item, valueReference: otherItems };
    }
    return { ...item, valueReference };
  });
  return newextensionList;
};

export const getPatientAvailability = (
  patientSlots: ISlotWithOperationType[] = [],
  isAlwaysAvailable: boolean
): PatientAvailability => {
  const hasPatientAvailabilityWith2Slots =
    patientSlots && patientSlots.length === 2;

  let slots;
  if (hasPatientAvailabilityWith2Slots) {
    slots = [];
    isAlwaysAvailable = true;
  } else {
    slots = patientSlots;
  }

  const availabileSlots =
    slots && slots?.filter((slot) => slot.status === SlotStatus.Free);

  const unavailabileSlot =
    slots && slots?.find((slot) => slot.status === SlotStatus.Busy);

  const exception = getExceptionSlot(unavailabileSlot, isAlwaysAvailable);

  let availability = {
    isAlwaysAvailable,
    availableSlots: [] as ISlotWithOperationType[],
    exception,
  };
  availability = {
    ...availability,
    availableSlots: availabileSlots.length
      ? convertFreeSlotDateToTime(availabileSlots)
      : DayIndexes.map((dayIndex) => createDefaultSlot(dayIndex)),
  };

  return availability;
};

function getExceptionSlot(unavailabile, isAlwaysAvailable) {
  return {
    slotId: unavailabile?.id || '',
    startDate: unavailabile?.start || '',
    endDate: unavailabile?.end || '',
    startTime:
      (unavailabile?.extension?.find(
        (ext) => ext.url === EXTENSION_URL_PATIENT_EXCEPTION_STARTTIME
      )?.valueString as string) || '',
    endTime:
      (unavailabile?.extension?.find(
        (ext) => ext.url === EXTENSION_URL_PATIENT_EXCEPTION_ENDTIME
      )?.valueString as string) || '',
    isRequired: !isAlwaysAvailable,
  };
}

export const createDefaultSlot = (dayIndex) => {
  const slot: ISlotWithOperationType = {
    id: `${TEMP_TEXT}${uuid()}`,
    resourceType: 'Slot',
    status: 'free',
    start: '',
    end: '',
    schedule: {},
    extension: [
      {
        url: weekDay,
        valuePositiveInt: dayIndex,
      },
      {
        url: SLOT_SEQUENCE_EXT,
        valueInteger: 1,
      },
      {
        url: isDayAvailable,
        valueBoolean: false,
      },
    ],
  };

  return slot;
};

export const getExceptionAvailabilitySlots = (exception) => {
  return [
    {
      id: exception?.slotId ? exception.slotId : undefined,
      resourceType: ResourceType.Slot,
      status: SlotStatus.Free,
      start: exception?.startDate,
      end: exception?.endDate,
      extension: [
        {
          url: EXTENSION_URL_PATIENT_EXCEPTION_STARTTIME,
          valueString: exception?.startTime,
        },
        {
          url: EXTENSION_URL_PATIENT_EXCEPTION_ENDTIME,
          valueString: exception?.endTime,
        },
      ],
    },
  ];
};

export const getAvailabilitySlots = ({
  isAlwaysAvailable,
  availableSlots,
  exception,
}) => {
  const freeSlots = isAlwaysAvailable
    ? availableSlots
        .map((slot) =>
          slot.id?.includes(TEMP_TEXT)
            ? null
            : { ...slot, operationType: 'DELETE' }
        )
        .filter((slot) => slot)
    : convertTimeToDate(
        sortFreeSlotsByStartTime(removeTempSlotIDs(availableSlots))
      );
  return [
    ...freeSlots,
    {
      id: exception?.slotId ? exception.slotId : undefined,
      resourceType: ResourceType.Slot,
      status: SlotStatus.Busy,
      start: exception?.startDate,
      end: exception?.endDate,
      extension: [
        {
          url: EXTENSION_URL_PATIENT_EXCEPTION_STARTTIME,
          valueString: exception?.startTime,
        },
        {
          url: EXTENSION_URL_PATIENT_EXCEPTION_ENDTIME,
          valueString: exception?.endTime,
        },
      ],
    },
  ];
};
export function convertFreeSlotDateToTime(slots: fhir.Slot[] = []) {
  const convertedSlots = slots?.map((slot) => {
    if (slot.status === SlotStatus.Free) {
      return {
        ...slot,
        start: getTimeFromZonedDateTime(slot.start) || slot.start,
        end: getTimeFromZonedDateTime(slot.end) || slot.end,
      };
    }
    return slot;
  });

  return convertedSlots;
}

export const isTimeRangeValid = (
  givenStartTime: boolean,
  value: string,
  startTime: string,
  endTime: string
): boolean => {
  const startDate = transformInput(givenStartTime ? value : startTime);
  const endDate = transformInput(givenStartTime ? endTime : value);

  return (
    isValid(startDate) &&
    isValid(endDate) &&
    isBefore(startDate as Date, endDate as Date)
  );
};

export const isDateRangeValid = (
  givenStartDate: boolean,
  value: string,
  startDate: string,
  endDate: string
): boolean => {
  const sDate = dateStringToObject(
    givenStartDate ? value : startDate,
    undefined
  );
  const eDate = dateStringToObject(givenStartDate ? endDate : value, undefined);

  return isValid(sDate) && isValid(eDate) && !isAfter(sDate, eDate);
};

export function setTimeForSlotsForWeekDays(
  slots: ISlotWithOperationType[],
  time,
  type,
  slotId
) {
  const modifiedSlots: ISlotWithOperationType[] = slots?.map((item) => {
    if (item?.id === slotId) {
      return {
        ...item,
        [type]: time,
      };
    }
    return item;
  });
  return modifiedSlots;
}

export function setTimeForSlotsForAllDays(
  slots: ISlotWithOperationType[],
  time,
  type,
  slotId
) {
  const modifiedSlots: ISlotWithOperationType[] = slots?.map((item) => {
    const slot = slots.find((slot) => slot.id === slotId) as fhir.Slot;
    if (
      getSlotSequence(item) === getSlotSequence(slot) &&
      (isGeneralWeekday(slot, item) || isGeneralWeekend(slot, item))
    ) {
      return {
        ...item,
        [type]: time,
      };
    }
    return item;
  });
  return modifiedSlots;
}
